/**
 * How many pieces the walls INSIDE a room actually cut it into.
 *
 * `findSplitForWall` asks a narrower question — "does THIS one wall, on its
 * own, divide the room" — by cutting the polygon with that wall's two face
 * lines. That is exact and vertex-preserving, and it is blind to the case the
 * gesture makes easy: a user draws one wall in from a corner, then a second
 * one meeting its free end, and between them they close off a region without
 * either wall spanning anything. The room is divided; neither wall divides it.
 *
 * This answers the wider question the only way that generalises: the free
 * floor is the room polygon MINUS the footprints of the walls standing in it,
 * and the regions are that difference's components. It handles one spanning
 * wall (2 regions, agreeing with the line cut), an L or U of several (2), and
 * several spanning walls (N) with no special cases.
 *
 * DETECTION ONLY, deliberately. `polygon-clipping` re-rounds every coordinate,
 * which would give up the exact-vertex property that keeps a wall carried
 * verbatim lying precisely on its room's boundary — so the split still builds
 * its polygons with the line cut, and this says whether that answer is the
 * whole story.
 *
 * Pure. X-Z plane.
 */

const polygonClipping = require('polygon-clipping');
const { signedAreaXZ } = require('../geometry/polygonSplit');
const { distanceToPolygonBoundary } = require('./geometry');
const { withSnapRetry } = require('../geometry/clipRetry');

/** A region smaller than this is numerical dust, not a piece of room. Matches
 *  the split's own sliver floor, so the two cannot disagree. */
const MIN_REGION_AREA_M2 = 1.0;
/** A wall face running this close to the polygon is ON the boundary. */
const ON_BOUNDARY_M = 0.12;
/** A wall is a boundary wall when at least this share of it runs along one. */
const BOUNDARY_SHARE = 0.5;
/** Footprints are grown by this so a face lying exactly on a region edge still
 *  cuts it rather than grazing it. */
const FOOTPRINT_GROW_M = 0.004;

/** The four corners of a structure wall's footprint, grown by `grow`. */
function wallFootprint(wall, grow = FOOTPRINT_GROW_M) {
  const ry = wall?.rotation?.y || 0;
  const halfLen = (wall?.dimensions?.x || 0) / 2 + grow;
  const halfThk = (wall?.dimensions?.z || 0) / 2 + grow;
  const dir = { x: Math.cos(ry), z: -Math.sin(ry) };
  const nrm = { x: Math.sin(ry), z: Math.cos(ry) };
  const c = wall.center;
  return [
    [-1, -1],
    [1, -1],
    [1, 1],
    [-1, 1],
  ].map(([a, b]) => [
    c.x + dir.x * halfLen * a + nrm.x * halfThk * b,
    c.z + dir.z * halfLen * a + nrm.z * halfThk * b,
  ]);
}

/**
 * The structure walls standing INSIDE `room` — the ones whose faces do not run
 * along its boundary. A boundary wall's footprint sits outside the polygon
 * (which runs along its inner face), so subtracting one removes nothing but
 * can nick a mitred corner into a sliver; excluding them is cheaper than
 * filtering the slivers afterwards.
 */
function interiorStructureWalls(room, structureWalls) {
  const ring = (room?.floors?.[0]?.polygonCorners || []).map((p) => ({
    x: p.x,
    z: p.z,
  }));
  if (ring.length < 3) return [];
  const byId = new Map(
    (structureWalls || []).filter((w) => w?.id).map((w) => [w.id, w])
  );
  const ids = new Set();
  for (const w of room.walls || []) {
    const bl = w?.alignment?.bottomLeft;
    const br = w?.alignment?.bottomRight;
    const sid = w?.alignment?.structureWallId;
    if (!bl || !br || !sid || !(w.dimensions?.x > 0.3)) continue;
    let on = 0;
    const samples = 10;
    for (let i = 0; i < samples; i++) {
      const t = i / (samples - 1);
      const p = { x: bl.x + (br.x - bl.x) * t, z: bl.z + (br.z - bl.z) * t };
      if (distanceToPolygonBoundary(p, ring) <= ON_BOUNDARY_M) on++;
    }
    if (on / samples < BOUNDARY_SHARE) ids.add(sid);
  }
  return [...ids].map((id) => byId.get(id)).filter(Boolean);
}

/**
 * The regions of free floor in `room`.
 *
 * @returns {{ regions: {x,z}[][], interiorWalls: object[], degenerate?: true }}
 *   One region means the room is undivided. `degenerate` marks a clip that
 *   failed at every snap grid — treated as undivided, never as a division.
 */
function roomRegions(room, structureWalls, opts = {}) {
  const minArea = opts.minArea ?? MIN_REGION_AREA_M2;
  const ring = (room?.floors?.[0]?.polygonCorners || []).map((p) => ({
    x: p.x,
    z: p.z,
  }));
  if (ring.length < 3) return { regions: [], interiorWalls: [] };

  const interiorWalls = interiorStructureWalls(room, structureWalls);
  if (interiorWalls.length === 0) return { regions: [ring], interiorWalls };

  const out = withSnapRetry((snap) => {
    const subject = [ring.map((p) => [snap(p.x), snap(p.z)])];
    const cuts = interiorWalls.map((w) => [
      wallFootprint(w).map(([x, z]) => [snap(x), snap(z)]),
    ]);
    return polygonClipping.difference(subject, ...cuts);
  });
  if (out) {
    const regions = out.value
      .map((poly) => {
        const r = poly[0].map(([x, z]) => ({ x, z }));
        const f = r[0];
        const l = r[r.length - 1];
        if (
          r.length > 1 &&
          Math.abs(f.x - l.x) < 1e-9 &&
          Math.abs(f.z - l.z) < 1e-9
        ) {
          r.pop();
        }
        return r;
      })
      .filter((r) => r.length >= 3 && Math.abs(signedAreaXZ(r)) >= minArea);
    return { regions, interiorWalls };
  }
  return { regions: [ring], interiorWalls, degenerate: true };
}

module.exports = {
  MIN_REGION_AREA_M2,
  ON_BOUNDARY_M,
  BOUNDARY_SHARE,
  wallFootprint,
  interiorStructureWalls,
  roomRegions,
};
